(() => {
  const init = (root) => {
    if (!root || root.dataset.contactInitialized === 'true') return;
    root.dataset.contactInitialized = 'true';

    const form = root.querySelector('.cs-form');
    const topic = root.querySelector('select[name="contact[support_topic]"]');
    const otherField = root.querySelector('[data-other-topic-field]');
    const otherInput = root.querySelector('[data-other-topic-input]');

    const clearFieldError = (field) => {
      const wrapper = field?.closest('.cs-field');
      if (!wrapper) return;

      wrapper.classList.remove('has-error');
      field.removeAttribute('aria-invalid');

      const error = wrapper.querySelector('.cs-field-error');
      if (error) error.textContent = '';

      if (field.dataset.validationOriginalDescribedby) {
        field.setAttribute('aria-describedby', field.dataset.validationOriginalDescribedby);
      } else {
        field.removeAttribute('aria-describedby');
      }
    };

    const getErrorMessage = (field) => {
      if (field.validity.valueMissing) {
        return root.dataset.requiredError || 'This field is required.';
      }

      if (field.type === 'email' && field.validity.typeMismatch) {
        return root.dataset.emailError || 'Please enter a valid email address.';
      }

      return field.validationMessage || root.dataset.requiredError || 'Please check this field.';
    };

    const renderFieldValidity = (field) => {
      if (!field || field.disabled || field.validity.valid) {
        clearFieldError(field);
        return true;
      }

      const wrapper = field.closest('.cs-field');
      if (!wrapper) return false;

      let error = wrapper.querySelector('.cs-field-error');
      if (!error) {
        error = document.createElement('span');
        error.className = 'cs-field-error';
        error.id = `${field.id || 'ContactField'}-error`;
        error.setAttribute('aria-live', 'polite');
        wrapper.appendChild(error);
        field.dataset.validationOriginalDescribedby = field.getAttribute('aria-describedby') || '';
      }

      error.textContent = getErrorMessage(field);
      wrapper.classList.add('has-error');
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute(
        'aria-describedby',
        [field.dataset.validationOriginalDescribedby, error.id].filter(Boolean).join(' ')
      );
      return false;
    };

    const toggleOtherTopic = () => {
      if (!topic || !otherField || !otherInput) return;

      const showOther = topic.selectedOptions[0]?.hasAttribute('data-other-topic-option') === true;
      otherField.hidden = !showOther;
      otherInput.disabled = !showOther;
      otherInput.required = showOther;
      otherInput.setAttribute('aria-required', showOther ? 'true' : 'false');

      if (!showOther) {
        otherInput.value = '';
        otherInput.dataset.touched = '';
        clearFieldError(otherInput);
      }
    };

    if (topic) {
      topic.addEventListener('change', () => {
        toggleOtherTopic();
        if (form?.classList.contains('was-validated') && otherInput && !otherInput.disabled) {
          renderFieldValidity(otherInput);
        }
      });
      toggleOtherTopic();
    }

    if (form) {
      const fields = Array.from(form.querySelectorAll('input:not([type="file"]), select, textarea'));

      fields.forEach((field) => {
        field.addEventListener('blur', () => {
          field.dataset.touched = 'true';
          renderFieldValidity(field);
        });

        const updateValidity = () => {
          if (field.dataset.touched === 'true' || form.classList.contains('was-validated')) {
            renderFieldValidity(field);
          }
        };

        field.addEventListener('input', updateValidity);
        field.addEventListener('change', updateValidity);
      });

      form.addEventListener('submit', (event) => {
        toggleOtherTopic();
        form.classList.add('was-validated');

        const invalidFields = fields.filter((field) => {
          if (field.disabled || !field.willValidate) return false;
          return !renderFieldValidity(field);
        });

        if (invalidFields.length > 0) {
          event.preventDefault();
          event.stopPropagation();
          invalidFields[0].focus({ preventScroll: true });
          invalidFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }

    const message = root.querySelector('[data-message-field]');
    const count = root.querySelector('[data-character-count]');
    if (message && count) {
      const updateCount = () => { count.textContent = `${message.value.length} / 1000`; };
      message.addEventListener('input', updateCount);
      updateCount();
    }

    const upload = root.querySelector('[data-upload-input]');
    const uploadName = root.querySelector('[data-upload-name]');
    const uploadError = root.querySelector('[data-upload-error]');
    if (upload && uploadName) {
      const maxFiles = Math.max(1, Number(root.dataset.uploadMaxFiles) || 5);
      const maxTotalBytes = Math.max(1, Number(root.dataset.uploadMaxTotalBytes) || 20 * 1024 * 1024);
      const maxTotalMegabytes = Math.round(maxTotalBytes / 1024 / 1024);
      const maxFilesError = (root.dataset.uploadMaxFilesError || 'You can upload up to [limit] files.')
        .replace('[limit]', String(maxFiles));
      const maxTotalError = (root.dataset.uploadMaxTotalError || 'Attachments must be [limit]MB or less in total.')
        .replace('[limit]', String(maxTotalMegabytes));
      let stagedInputs = [];

      const clearStagedInputs = () => {
        stagedInputs.forEach((input) => input.remove());
        stagedInputs = [];
        upload.setAttribute('name', 'contact[attachment]');
      };

      const setUploadError = (message = '') => {
        const wrapper = upload.closest('.cs-field');
        wrapper?.classList.toggle('has-error', Boolean(message));
        upload.setAttribute('aria-invalid', message ? 'true' : 'false');

        if (uploadError) {
          uploadError.id ||= `${upload.id}-error`;
          uploadError.textContent = message;
          if (message) upload.setAttribute('aria-describedby', uploadError.id);
          else upload.removeAttribute('aria-describedby');
        }
      };

      const stageFilesWithUniqueNames = (files) => {
        clearStagedInputs();
        if (!form || files.length === 0 || typeof DataTransfer === 'undefined') return;

        try {
          files.forEach((file, index) => {
            const transfer = new DataTransfer();
            transfer.items.add(file);

            const stagedInput = document.createElement('input');
            stagedInput.type = 'file';
            stagedInput.name = `contact[attachment_${index + 1}]`;
            stagedInput.files = transfer.files;
            stagedInput.hidden = true;
            stagedInput.tabIndex = -1;
            stagedInput.setAttribute('aria-hidden', 'true');
            form.appendChild(stagedInput);
            stagedInputs.push(stagedInput);
          });

          upload.removeAttribute('name');
        } catch (error) {
          clearStagedInputs();
        }
      };

      const validateAndStageFiles = () => {
        const files = Array.from(upload.files || []);
        uploadName.textContent = files.map((file) => file.name).join(' · ');

        if (files.length > maxFiles) {
          clearStagedInputs();
          upload.value = '';
          uploadName.textContent = '';
          setUploadError(maxFilesError);
          return false;
        }

        const totalBytes = files.reduce((total, file) => total + file.size, 0);
        if (totalBytes > maxTotalBytes) {
          clearStagedInputs();
          upload.value = '';
          uploadName.textContent = '';
          setUploadError(maxTotalError);
          return false;
        }

        setUploadError();
        stageFilesWithUniqueNames(files);
        return true;
      };

      upload.addEventListener('change', validateAndStageFiles);

      form?.addEventListener('submit', (event) => {
        if (validateAndStageFiles()) return;

        event.preventDefault();
        event.stopPropagation();
        upload.closest('.cs-field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-contact-support]')) init(scope);
    scope.querySelectorAll?.('[data-contact-support]').forEach(init);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
